/**
 * Loyalty: points a guest earns on paid tabs and spends on the bill.
 *
 * The server does every sum. It earns the points when a tab closes, works out
 * what a number of points takes off, how many a guest may spend on this tab,
 * and which tier they are in; the screens show what it says.
 *
 * Points spent come off the bill as money off, "Points" — a discount, not a
 * way of paying — so the tax on the day, the takings and the drawer all see
 * money the guest actually paid.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface LoyaltySiteRate {
  location_id: string;
  points_per_unit: string;
}

export interface LoyaltySettings {
  /** False until somebody first saves the scheme; the figures are then its starting values. */
  configured: boolean;
  is_active: boolean;
  /** Points for each whole unit of money paid, after discounts, tips and service charge left out. */
  points_per_unit: string;
  /** `redeem_points` points take `redeem_value` off a bill. */
  redeem_points: number;
  redeem_value: string;
  /** The fewest points spent at once. 0: any. */
  min_redeem: number;
  /** How much of a bill points may pay, per cent. Null: all of it. */
  max_bill_percent: string | null;
  /** Months without a visit before a balance lapses. Null: never. */
  expiry_months: number | null;
  site_rates: LoyaltySiteRate[];
}

export interface LoyaltySettingsInput {
  is_active: boolean;
  points_per_unit: string;
  redeem_points: number;
  redeem_value: string;
  min_redeem: number;
  max_bill_percent: string | null;
  expiry_months: number | null;
}

export interface LoyaltyTier {
  id: string;
  name: string;
  /** Points earned in the last twelve months that reach it. */
  threshold: number;
  multiplier: string;
}

export interface LoyaltyReward {
  id: string;
  name: string;
  points: number;
  /** `item`: a dish on the house. `percent`: off the whole bill. */
  kind: 'item' | 'percent';
  item_id: string | null;
  /** What the menu calls the dish now. Null when it has gone from the menu. */
  item_name: string | null;
  percent: string | null;
  is_active: boolean;
}

export interface LoyaltyRewardInput {
  name: string;
  points: number;
  kind: 'item' | 'percent';
  item_id?: string | null;
  percent?: string | null;
  is_active?: boolean;
}

export interface LoyaltyAccount {
  customer_id: string;
  balance: number;
  /** Earned in the last twelve months, less what refunds took back. */
  earned_last_year: number;
  /** Null without tiers on the plan, or below the first tier. */
  tier: LoyaltyTier | null;
  next_tier: LoyaltyTier | null;
  /** When the balance lapses if they do not come back. Null: it does not. */
  expires_at: string | null;
  last_activity_at: string | null;
}

export type LoyaltyEntryKind =
  'earn' | 'reversed' | 'redeem' | 'returned' | 'adjust' | 'expire' | 'forfeit';

export const LOYALTY_ENTRY_LABELS: Record<LoyaltyEntryKind, string> = {
  earn: 'Earned',
  reversed: 'Refund took back',
  redeem: 'Spent',
  returned: 'Given back',
  adjust: 'Changed by hand',
  expire: 'Lapsed',
  forfeit: 'Ended on erasure',
};

export interface LoyaltyEntry {
  id: string;
  kind: LoyaltyEntryKind;
  /** Signed: earned and given back are positive. */
  points: number;
  /** What the bill earned on, what the points took off, what was refunded. */
  amount: string | null;
  order_id: string | null;
  location_id: string | null;
  reward_id: string | null;
  multiplier: string | null;
  tier_name: string | null;
  note: string | null;
  actor_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface TabRedemption {
  discount_id: string;
  points: number;
  amount: string;
  reward_id: string | null;
  note: string | null;
}

export interface TabLoyalty {
  /** Null when nobody is on the tab. */
  customer_id: string | null;
  /** Whether points are switched on here. */
  active: boolean;
  account: LoyaltyAccount | null;
  redeem_points: number;
  redeem_value: string;
  min_redeem: number;
  /** The most the guest can spend on this tab now, and what that takes off. */
  max_points: number;
  max_value: string;
  rewards: { reward: LoyaltyReward; unavailable: string | null }[];
  /** Points spent on this tab and still on it. */
  redeemed: TabRedemption[];
}

function useLoyaltyMutation<TInput, TResult>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
  keys: string[][],
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      for (const key of keys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

// --------------------------------------------------------------------------- //
// The scheme (back office)
// --------------------------------------------------------------------------- //

export function useLoyaltySettings(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['loyalty', 'settings'],
    queryFn: ({ signal }) => api.get<LoyaltySettings>('/v1/loyalty/settings', signal),
    enabled: options?.enabled ?? true,
  });
}

/** On the audit log. 402 below Professional. */
export function useSaveLoyaltySettings() {
  return useLoyaltyMutation<LoyaltySettingsInput, LoyaltySettings>(
    (api, body) =>
      api.request<LoyaltySettings>('/v1/loyalty/settings', { method: 'PUT', body }),
    [['loyalty'], ['customers']],
  );
}

/** A site's own earning rate; null puts it back on the scheme's. */
export function useSetLoyaltySiteRate() {
  return useLoyaltyMutation<
    { locationId: string; pointsPerUnit: string | null },
    LoyaltySettings
  >(
    (api, { locationId, pointsPerUnit }) =>
      pointsPerUnit === null
        ? api.request<LoyaltySettings>(`/v1/loyalty/sites/${locationId}`, { method: 'DELETE' })
        : api.request<LoyaltySettings>(`/v1/loyalty/sites/${locationId}`, {
            method: 'PUT',
            body: { points_per_unit: pointsPerUnit },
          }),
    [['loyalty']],
  );
}

export function useLoyaltyTiers(options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['loyalty', 'tiers'],
    queryFn: ({ signal }) => api.get<LoyaltyTier[]>('/v1/loyalty/tiers', signal),
    enabled: options?.enabled ?? true,
  });
}

/** Adds a tier, or changes one when `id` is given. Enterprise. */
export function useSaveLoyaltyTier() {
  return useLoyaltyMutation<
    { id?: string | undefined; name: string; threshold: number; multiplier: string },
    LoyaltyTier
  >(
    (api, { id, ...body }) =>
      id
        ? api.request<LoyaltyTier>(`/v1/loyalty/tiers/${id}`, { method: 'PUT', body })
        : api.post<LoyaltyTier>('/v1/loyalty/tiers', body),
    [['loyalty'], ['customers']],
  );
}

export function useDeleteLoyaltyTier() {
  return useLoyaltyMutation<{ id: string }, void>(
    (api, { id }) => api.request<void>(`/v1/loyalty/tiers/${id}`, { method: 'DELETE' }),
    [['loyalty'], ['customers']],
  );
}

export function useLoyaltyRewards(options?: { enabled?: boolean; includeRetired?: boolean }) {
  const api = useApi();
  const retired = options?.includeRetired ?? false;
  return useQuery({
    queryKey: ['loyalty', 'rewards', retired],
    queryFn: ({ signal }) =>
      api.get<LoyaltyReward[]>(`/v1/loyalty/rewards?include_retired=${retired}`, signal),
    enabled: options?.enabled ?? true,
  });
}

/** Adds a reward, or changes (or retires) one when `id` is given. Enterprise. */
export function useSaveLoyaltyReward() {
  return useLoyaltyMutation<{ id?: string | undefined } & LoyaltyRewardInput, LoyaltyReward>(
    (api, { id, ...body }) =>
      id
        ? api.request<LoyaltyReward>(`/v1/loyalty/rewards/${id}`, { method: 'PUT', body })
        : api.post<LoyaltyReward>('/v1/loyalty/rewards', body),
    [['loyalty']],
  );
}

// --------------------------------------------------------------------------- //
// A guest's points
// --------------------------------------------------------------------------- //

export function useLoyaltyAccount(
  customerId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'loyalty', customerId ?? null],
    queryFn: ({ signal }) =>
      api.get<LoyaltyAccount>(`/v1/customers/${customerId}/loyalty`, signal),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
    staleTime: 10_000,
  });
}

export function useLoyaltyLedger(customerId: string | null | undefined, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['customers', 'loyalty-ledger', customerId ?? null],
    queryFn: ({ signal }) =>
      api.get<LoyaltyEntry[]>(`/v1/customers/${customerId}/loyalty/ledger`, signal),
    enabled: Boolean(customerId) && enabled,
  });
}

/** Points on (positive) or off (negative), with a reason. On the audit log. */
export function useAdjustPoints() {
  return useLoyaltyMutation<{ id: string; points: number; reason: string }, LoyaltyAccount>(
    (api, { id, ...body }) =>
      api.post<LoyaltyAccount>(`/v1/customers/${id}/loyalty/adjust`, body),
    [['customers']],
  );
}

// --------------------------------------------------------------------------- //
// Points on a tab (till)
// --------------------------------------------------------------------------- //

/**
 * The guest's balance, what they could spend on this tab, and what is on it.
 * Keyed under the order, so anything that changes the tab refreshes it.
 */
export function useTabLoyalty(orderId: string, options?: { enabled?: boolean }) {
  const api = useApi();
  return useQuery({
    queryKey: ['orders', 'detail', orderId, 'loyalty'],
    queryFn: ({ signal }) => api.get<TabLoyalty>(`/v1/orders/${orderId}/loyalty`, signal),
    enabled: options?.enabled ?? true,
  });
}

function useTabPointsMutation<TInput extends { orderId: string }>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TabLoyalty>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TabLoyalty, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: (_data, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: ['orders', 'detail', input.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', 'ledger', input.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['customers', 'loyalty'] });
    },
  });
}

/** 409 saying why: too few, too many for this bill, or more than they have. */
export function useRedeemPoints() {
  return useTabPointsMutation<{ orderId: string; points: number }>((api, input) =>
    api.post<TabLoyalty>(`/v1/orders/${input.orderId}/loyalty/redeem`, {
      points: input.points,
    }),
  );
}

/** Something from the rewards list. A free dish has to be on the tab first. */
export function useRedeemReward() {
  return useTabPointsMutation<{ orderId: string; rewardId: string; lineId?: string | null }>(
    (api, input) =>
      api.post<TabLoyalty>(`/v1/orders/${input.orderId}/loyalty/rewards`, {
        reward_id: input.rewardId,
        line_id: input.lineId ?? null,
      }),
  );
}

/** Take points spent on this tab off it, and put them back on the balance. */
export function useGiveBackPoints() {
  return useTabPointsMutation<{ orderId: string; discountId: string }>((api, input) =>
    api.post<TabLoyalty>(
      `/v1/orders/${input.orderId}/loyalty/redemptions/${input.discountId}/return`,
    ),
  );
}

/** The reason code points come off a bill under, so a tab can tell them apart. */
export const POINTS_REASON = 'loyalty_points';
